import { Sparkles } from "lucide-react-native";
import {
  ComponentRef,
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Platform, Text as RNText, ScrollView } from "react-native";

import {
  Pressable,
  Spinner,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { QuestionSuggestion } from "@/types/responses";
import { QuestionType } from "@/types/utils";

import { useQuestionVariables } from "./QuestionVariablesContext";
import { toVariableSegments, variableTypeMap } from "./variableHighlight";

const TYPEWRITER_INTERVAL_MS = 22;
const OPTION_REVEAL_DELAY_MS = 250;

/* Explicit metrics shared by the transparent input and the highlight mirror so
   the colored tokens line up exactly with the typed text. The mirror uses a
   plain RN `Text` (not the gluestack one, which treats `lineHeight` as a ratio
   and would blow the mirror up to fontSize×lineHeight px). The input text is
   rendered transparent so the mirror's colors show through, while the caret
   stays visible. */
const FONT_SIZE = 16;
const LINE_HEIGHT = 24;
const PLAIN_COLOR = "rgb(38, 38, 39)"; // typography-900
const KNOWN_COLOR = "rgb(124, 58, 237)"; // primary-600
const IMAGE_COLOR = "rgb(11, 141, 205)"; // info-600
const UNKNOWN_COLOR = "rgb(220, 38, 38)"; // error-600

interface QuestionTextInputProps {
  disabled?: boolean;
  lines: string[];
  onChange: (text: string) => void;
  onLoadingChange?: (loading: boolean) => void;
  onSuggest: (suggestion: QuestionSuggestion) => void;
  options: string[];
  text: string;
  type: QuestionType;
}

const QuestionTextInput: FC<QuestionTextInputProps> = ({
  disabled = false,
  lines,
  onChange,
  onLoadingChange,
  onSuggest,
  options,
  text,
  type,
}) => {
  const { i18n } = useLocalization();
  const { loading, suggestQuestions } = API.AI.useSuggestQuestions();
  const variables = useQuestionVariables();
  const inputRef = useRef<ComponentRef<typeof TextareaInput>>(null);
  const outerRef = useRef<ComponentRef<typeof VStack>>(null);
  const markerRef = useRef<ComponentRef<typeof RNText>>(null);
  const [animatingText, setAnimatingText] = useState<string | null>(null);
  const [dismissedQuery, setDismissedQuery] = useState<string | null>(null);
  const [caret, setCaret] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [caretPos, setCaretPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const animateText = (targetText: string) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setAnimatingText("");
    let index = 0;
    intervalRef.current = setInterval(() => {
      index++;
      setAnimatingText(targetText.slice(0, index));
      if (index >= targetText.length) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setAnimatingText(null);
      }
    }, TYPEWRITER_INTERVAL_MS);
  };

  const handleSuggest = async () => {
    const suggestion = await suggestQuestions({
      lines,
      options,
      text: text || null,
      type,
    });
    if (!suggestion) return;

    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    animateText(suggestion.text);

    const maxItems = Math.max(
      suggestion.options.length,
      suggestion.lines.length
    );

    onSuggest({
      lines: suggestion.lines.length > 0 ? [] : suggestion.lines,
      options: suggestion.options.length > 0 ? [] : suggestion.options,
      text: suggestion.text,
    });

    for (let i = 1; i <= maxItems; i++) {
      const t = setTimeout(() => {
        onSuggest({
          lines: suggestion.lines.slice(0, i),
          options: suggestion.options.slice(0, i),
          text: suggestion.text,
        });
      }, i * OPTION_REVEAL_DELAY_MS);
      timeoutsRef.current.push(t);
    }
  };

  const isAnimating = animatingText !== null;
  const displayText = isAnimating ? animatingText! : text;
  const canSuggest = text.trim().length >= 30;

  const types = useMemo(() => variableTypeMap(variables), [variables]);
  const segments = useMemo(
    () => toVariableSegments(displayText, types),
    [displayText, types]
  );

  /* Detect the `{{` token the caret currently sits in. We key off the caret so
     autocomplete triggers wherever the user types `{{`, not just at the end. */
  const openToken = useMemo(() => {
    if (isAnimating || variables.length === 0) return null;
    const before = text.slice(0, caret);
    const open = before.lastIndexOf("{{");
    if (open === -1) return null;
    const middle = text.slice(open + 2, caret);
    if (middle.includes("}}") || middle.includes("\n")) return null;
    return { end: caret, open, query: middle.trim() };
  }, [caret, isAnimating, text, variables.length]);

  const suggestions = useMemo(() => {
    if (!openToken) return [];
    const q = openToken.query.toLowerCase();
    return variables.filter((v) => v.name.toLowerCase().includes(q));
  }, [openToken, variables]);

  const showSuggestions =
    !!openToken && suggestions.length > 0 && dismissedQuery !== openToken.query;

  const syncCaret = () => {
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (node && typeof node.selectionStart === "number") {
      setCaret(node.selectionStart);
    }
  };

  const handleChange = (next: string) => {
    onChange(next);
    syncCaret();
  };

  /* Keep the highlight mirror aligned with the input while it scrolls. RN gives
     `contentOffset.y`; on web the event lacks it, so we read the node directly. */
  const handleScroll = (e: {
    nativeEvent?: { contentOffset?: { y: number } };
  }) => {
    const offset = e.nativeEvent?.contentOffset?.y;
    if (typeof offset === "number") {
      setScrollTop(offset);
      return;
    }
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (node) setScrollTop(node.scrollTop);
  };

  /* Measure the caret's VIEWPORT position (web) so the dropdown — portaled to
     <body>, see below — opens right next to it. A hidden mirror duplicates the
     text up to the caret and a zero-width marker gives us the coordinates.
     Viewport coords (not container-relative) because the menu is fixed-position
     in a body portal. Re-measured before paint on every reopen. */
  useLayoutEffect(() => {
    if (Platform.OS !== "web" || !showSuggestions) return;
    const marker = markerRef.current as unknown as HTMLElement | null;
    if (!marker?.getBoundingClientRect) return;
    const m = marker.getBoundingClientRect();
    setCaretPos({ left: m.left, top: m.bottom + 4 });
  }, [showSuggestions, text, caret, scrollTop]);

  const insertVariable = (name: string) => {
    if (!openToken) return;
    const insert = `{{${name}}}`;
    const next =
      text.slice(0, openToken.open) + insert + text.slice(openToken.end);
    const nextCaret = openToken.open + insert.length;
    onChange(next);
    setCaret(nextCaret);
    setDismissedQuery(null);
    requestAnimationFrame(() => {
      const node = inputRef.current as unknown as HTMLTextAreaElement | null;
      node?.focus?.();
      node?.setSelectionRange?.(nextCaret, nextCaret);
    });
  };

  const menuItems = (
    <ScrollView keyboardShouldPersistTaps="handled">
      {suggestions.map((v) => (
        <Pressable
          key={v.name}
          className="px-3 py-2 hover:bg-background-100"
          onPress={() => insertVariable(v.name)}
        >
          <Text className="text-sm text-typography-900">{v.name}</Text>
        </Pressable>
      ))}
    </ScrollView>
  );

  /* The menu must escape the editor's stacking context: a parent question card
     uses an Animated transform, which becomes the containing block for any
     absolute/fixed descendant — so no z-index here can lift the menu above the
     sibling "Options" block. On web we portal it to <body> at fixed viewport
     coords next to the caret; native has no such trap, so it stays an absolute
     child of the input container. */
  const menu =
    Platform.OS === "web" && caretPos ? (
      createPortal(
        <div
          style={{
            position: "fixed",
            left: caretPos.left,
            top: caretPos.top,
            zIndex: 9999,
          }}
        >
          <VStack className="w-56 max-h-40 overflow-hidden rounded-xl border border-outline-200 bg-background-0 shadow-md">
            {menuItems}
          </VStack>
        </div>,
        document.body
      )
    ) : (
      <VStack
        className="absolute w-56 max-h-40 overflow-hidden rounded-xl border border-outline-200 bg-background-0 shadow-md"
        style={{ left: 0, top: "100%", zIndex: 1000 }}
      >
        {menuItems}
      </VStack>
    );

  return (
    <VStack
      ref={outerRef}
      className="relative flex-1"
      // Lift the whole editor above neighbouring question cards while the
      // suggestions are open so the dropdown isn't painted over.
      style={showSuggestions ? { zIndex: 50 } : undefined}
    >
      <Textarea
        className="relative flex-1 min-h-[70px] h-auto border border-outline-200 rounded-xl bg-background-50"
        isDisabled={disabled || loading || isAnimating}
      >
        {/* Highlight mirror: transparent glyphs, colored `{{tokens}}`, sitting
            under the real (visible) input text. Clipped to the box and shifted
            by the input's scroll offset so the tokens track the typed text. */}
        <VStack
          className="absolute inset-0 px-2 py-2 pr-9 pointer-events-none overflow-hidden"
          style={{ pointerEvents: "none", zIndex: 0 }}
        >
          <RNText
            style={{
              fontSize: FONT_SIZE,
              lineHeight: LINE_HEIGHT,
              transform: [{ translateY: -scrollTop }],
              // @ts-expect-error - web-only wrapping style to mirror the textarea
              whiteSpace: "pre-wrap",
            }}
          >
            {segments.map((s) => (
              <RNText
                key={s.key}
                style={{
                  color: s.token
                    ? s.type === "image"
                      ? IMAGE_COLOR
                      : s.known
                        ? KNOWN_COLOR
                        : UNKNOWN_COLOR
                    : PLAIN_COLOR,
                  fontSize: FONT_SIZE,
                  lineHeight: LINE_HEIGHT,
                }}
              >
                {s.text}
              </RNText>
            ))}
          </RNText>
        </VStack>
        {/* Hidden mirror used only to measure the caret's pixel position so the
            suggestions dropdown can open right next to it (web only). */}
        {Platform.OS === "web" && !!openToken && (
          <VStack
            className="absolute inset-0 px-2 py-2 pr-9 pointer-events-none"
            style={{ opacity: 0, pointerEvents: "none", zIndex: 0 }}
          >
            <RNText
              style={{
                fontSize: FONT_SIZE,
                lineHeight: LINE_HEIGHT,
                transform: [{ translateY: -scrollTop }],
                // @ts-expect-error - web-only wrapping style to mirror the textarea
                whiteSpace: "pre-wrap",
              }}
            >
              {text.slice(0, caret)}
              <RNText ref={markerRef}>{"\u200b"}</RNText>
            </RNText>
          </VStack>
        )}
        <TextareaInput
          ref={inputRef}
          className="pr-9"
          onChangeText={handleChange}
          onFocus={syncCaret}
          onScroll={handleScroll}
          onSelectionChange={(e) => setCaret(e.nativeEvent.selection.start)}
          placeholder={i18n.t("questions.questionPlaceholder")}
          selectionColor={PLAIN_COLOR}
          style={{
            backgroundColor: "transparent",
            color: "transparent",
            fontSize: FONT_SIZE,
            lineHeight: LINE_HEIGHT,
            position: "relative",
            zIndex: 1,
            ...(Platform.OS === "web" ? { caretColor: PLAIN_COLOR } : null),
          }}
          value={displayText}
        />
        <Pressable
          className="absolute top-2 right-2 p-1 rounded-md disabled:opacity-40"
          disabled={disabled || loading || !canSuggest}
          onPress={handleSuggest}
        >
          {loading ? (
            <Spinner className="text-primary-400" size="small" />
          ) : (
            <Sparkles className="text-primary-400" size={16} />
          )}
        </Pressable>
      </Textarea>

      {showSuggestions && menu}
    </VStack>
  );
};

export default QuestionTextInput;
